"use client";

import { useState } from "react";
import { ArrowRight, ChevronDown, Download, Minus, Plus } from "lucide-react";
import Link from "next/link";

import { EUR } from "~/lib/currency";
import type { TherapyPriceMarketSnapshot } from "~/server/marketing/therapy-price-market";

function formatDate(value: string): string {
  return new Intl.DateTimeFormat("sr-Latn-RS", {
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  })
    .format(new Date(value))
    .replace(/\.$/, "");
}

function PriceComparison({
  snapshot,
}: {
  snapshot: TherapyPriceMarketSnapshot;
}) {
  const columns = [
    { title: "Individualna", data: snapshot.individual },
    ...(snapshot.couples
      ? [{ title: "Za parove", data: snapshot.couples }]
      : []),
  ];
  return (
    <table className="my-6 w-full table-fixed border-collapse text-left text-sm sm:my-8">
      <caption className="sr-only">
        Poređenje cena i trajanja online seansi iz uzorka terapeuta
      </caption>
      <thead>
        <tr className="border-border border-b">
          <th
            scope="col"
            className="text-muted-foreground w-[34%] pr-2 pb-3 text-xs font-normal"
          >
            Po seansi
          </th>
          {columns.map(({ title }) => (
            <th
              key={title}
              scope="col"
              className="pb-3 pl-2 text-right font-bold sm:text-lg"
            >
              {title}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        <tr>
          <th scope="row" className="py-4 pr-2 font-normal">
            Medijana
          </th>
          {columns.map(({ title, data }) => (
            <td
              key={title}
              className="text-brand py-4 pl-2 text-right text-3xl leading-none tracking-tight tabular-nums sm:text-5xl"
            >
              {EUR.format(data.median)}
            </td>
          ))}
        </tr>
        <tr className="border-border border-t">
          <th
            scope="row"
            className="text-muted-foreground py-3 pr-2 font-normal"
          >
            Raspon cena
          </th>
          {columns.map(({ title, data }) => (
            <td
              key={title}
              className="py-3 pl-2 text-right text-xs tabular-nums sm:text-sm"
            >
              {EUR.format(data.min)}–{EUR.format(data.max)}
            </td>
          ))}
        </tr>
        <tr className="border-border border-y">
          <th
            scope="row"
            className="text-muted-foreground py-3 pr-2 font-normal"
          >
            Najčešće trajanje
          </th>
          {columns.map(({ title, data }) => (
            <td key={title} className="py-3 pl-2 text-right tabular-nums">
              {data.typicalDuration} min
            </td>
          ))}
        </tr>
      </tbody>
    </table>
  );
}

export function TherapyPriceMonitor({
  snapshot,
}: {
  snapshot: TherapyPriceMarketSnapshot | null;
}) {
  const [priceInput, setPriceInput] = useState(
    String(snapshot?.individual.median ?? 45),
  );
  const [sessionsInput, setSessionsInput] = useState("4");
  if (!snapshot) return null;

  const sessionPrice = Math.min(500, Math.max(15, Number(priceInput) || 15));
  const sessionsPerMonth = Math.min(
    31,
    Math.max(1, Number(sessionsInput) || 1),
  );
  const monthlyCost = sessionsPerMonth * sessionPrice;
  function changeSessions(value: number) {
    const next = Math.min(31, Math.max(1, value));
    setSessionsInput(String(next));
  }

  return (
    <section
      className="not-prose text-foreground border-border my-12 border-y py-8 sm:my-16 sm:py-10"
      aria-labelledby="therapy-price-monitor-heading"
    >
      <div className="flex flex-wrap items-center justify-between gap-2 text-xs">
        <p className="text-brand font-bold tracking-[0.12em] uppercase">
          Rešimo Probleme · Pregled cena
        </p>
        <span className="text-muted-foreground">
          {formatDate(snapshot.updatedAt)}
        </span>
      </div>
      <h2
        id="therapy-price-monitor-heading"
        className="mt-5 max-w-lg text-3xl leading-[1.12] tracking-tight sm:text-4xl"
      >
        Koliko trenutno koštaju seanse
      </h2>
      <p className="text-muted-foreground mt-4 max-w-xl text-base leading-relaxed">
        Pregled cena na osnovu podataka {snapshot.sampleLabel} terapeuta iz
        Srbije koji nude online psihoterapiju na srpskom, ljudima u Srbiji i
        širom sveta.
      </p>

      <PriceComparison snapshot={snapshot} />

      <div className="border-border bg-secondary mt-4 overflow-hidden rounded-xl border">
        <div className="p-5 sm:p-7">
          <h3 className="text-2xl leading-tight tracking-tight">
            Koliko da izdvojiš mesečno?
          </h3>
          <p className="text-muted-foreground mt-2 text-sm leading-relaxed">
            Prilagodi broj seansi i cenu svom planu.
          </p>
          <div className="mt-6 grid gap-6 sm:grid-cols-2">
            <div>
              <label htmlFor="budget-sessions" className="text-sm font-bold">
                Seansi mesečno
              </label>
              <div className="border-input bg-background mt-2 flex h-12 items-center rounded-lg border">
                <button
                  type="button"
                  aria-label="Jedna seansa manje"
                  disabled={sessionsPerMonth <= 1}
                  onClick={() => changeSessions(sessionsPerMonth - 1)}
                  className="focus-visible:ring-brand flex h-11 w-11 shrink-0 items-center justify-center rounded-md outline-none focus-visible:ring-2 disabled:opacity-30"
                >
                  <Minus size={16} aria-hidden="true" />
                </button>
                <input
                  id="budget-sessions"
                  type="text"
                  inputMode="numeric"
                  value={sessionsInput}
                  onChange={(event) => {
                    const value = event.target.value;
                    if (!/^\d*$/.test(value)) return;
                    setSessionsInput(value);
                  }}
                  onBlur={() => changeSessions(sessionsPerMonth)}
                  className="focus-visible:ring-brand h-full w-full min-w-0 bg-transparent text-center text-lg tabular-nums outline-none focus-visible:ring-2"
                />
                <button
                  type="button"
                  aria-label="Jedna seansa više"
                  disabled={sessionsPerMonth >= 31}
                  onClick={() => changeSessions(sessionsPerMonth + 1)}
                  className="focus-visible:ring-brand flex h-11 w-11 shrink-0 items-center justify-center rounded-md outline-none focus-visible:ring-2 disabled:opacity-30"
                >
                  <Plus size={16} aria-hidden="true" />
                </button>
              </div>
              <div
                className="mt-2 flex gap-2"
                aria-label="Brzi izbor broja seansi"
              >
                {[2, 4, 8].map((count) => (
                  <button
                    key={count}
                    type="button"
                    aria-pressed={sessionsPerMonth === count}
                    onClick={() => changeSessions(count)}
                    className={`focus-visible:ring-brand min-h-10 flex-1 rounded-md border text-sm transition-colors outline-none focus-visible:ring-2 ${sessionsPerMonth === count ? "border-brand bg-brand text-white" : "border-input hover:bg-background"}`}
                  >
                    {count} mesečno
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label htmlFor="budget-price" className="text-sm font-bold">
                Cena jedne seanse u EUR
              </label>
              <div className="relative mt-2">
                <input
                  id="budget-price"
                  type="number"
                  min={15}
                  max={500}
                  value={priceInput}
                  onChange={(event) => setPriceInput(event.target.value)}
                  onBlur={() => setPriceInput(String(sessionPrice))}
                  className="border-input bg-background focus-visible:ring-brand h-12 w-full rounded-lg border pr-12 pl-4 text-lg tabular-nums outline-none focus-visible:ring-2"
                />
                <span
                  aria-hidden="true"
                  className="text-muted-foreground pointer-events-none absolute top-3 right-4"
                >
                  €
                </span>
              </div>
              <p className="text-muted-foreground mt-3 text-xs leading-relaxed">
                Početna vrednost je medijana cena individualnih seansi. Možeš da
                uneseš drugu cenu.
              </p>
            </div>
          </div>
        </div>

        <div
          className="bg-brand-forest px-5 py-6 text-white sm:px-7"
          role="status"
          aria-live="polite"
          aria-atomic="true"
        >
          <div className="flex flex-wrap items-end justify-between gap-5">
            <div>
              <p className="text-sm text-white/80">Tvoj mesečni budžet</p>
              <p className="mt-2 text-5xl leading-none tracking-tight tabular-nums">
                {EUR.format(monthlyCost)}
              </p>
            </div>
            <dl className="flex gap-6 pb-1 sm:gap-8">
              <div>
                <dt className="text-xs text-white/80">Za 3 meseca</dt>
                <dd className="mt-1 text-xl tabular-nums">
                  {EUR.format(monthlyCost * 3)}
                </dd>
              </div>
              <div>
                <dt className="text-xs text-white/80">Za 6 meseci</dt>
                <dd className="mt-1 text-xl tabular-nums">
                  {EUR.format(monthlyCost * 6)}
                </dd>
              </div>
            </dl>
          </div>
        </div>
      </div>

      <div className="text-muted-foreground mt-3 flex flex-wrap items-center justify-between gap-x-4 gap-y-1 text-xs">
        <span>Računica ostaje samo u tvom pregledaču.</span>
        <button
          type="button"
          onClick={() => window.print()}
          className="focus-visible:ring-brand hover:text-brand inline-flex min-h-11 items-center gap-2 rounded-sm outline-none focus-visible:ring-2"
        >
          <Download size={14} aria-hidden="true" />
          Sačuvaj ili odštampaj računicu
        </button>
      </div>

      <details className="group border-border mt-5 border-t pt-2 text-sm">
        <summary className="focus-visible:ring-brand flex min-h-12 cursor-pointer list-none items-center justify-between gap-3 rounded-sm outline-none focus-visible:ring-2 [&::-webkit-details-marker]:hidden">
          O podacima i načinu računanja
          <ChevronDown
            size={16}
            className="shrink-0 transition-transform group-open:rotate-180 motion-reduce:transition-none"
            aria-hidden="true"
          />
        </summary>
        <div className="text-muted-foreground space-y-3 pb-5 leading-relaxed">
          <p>
            Pregled se zasniva na cenama {snapshot.sampleLabel} terapeuta iz
            Srbije koji preko naše platforme nude online psihoterapiju na
            srpskom, ljudima u Srbiji i širom sveta. Pregled se odnosi na
            njihove cene, ne na celokupno tržište. Poslednja izmena podataka:{" "}
            {formatDate(snapshot.updatedAt)}.
          </p>
          <p>
            Medijana deli poređane cene na dve polovine: najmanje polovina
            terapeuta naplaćuje toliko ili manje, a najmanje polovina toliko ili
            više. Na primer, za cene 30, 40 i 80 € medijana je 40 €. Kada je
            broj cena paran, uzimamo prosek dve cene u sredini. Raspon pokazuje
            najnižu i najvišu cenu.
          </p>
          <p>
            Računica množi unetu cenu brojem seansi. Ne uključuje popuste i ne
            predstavlja preporuku koliko često ili koliko dugo treba ići na
            terapiju.
          </p>
        </div>
      </details>

      <Link
        href="/pristupacna-cena-psihoterapije"
        className="text-brand focus-visible:ring-brand group mt-4 inline-flex min-h-11 items-center gap-3 rounded-sm text-sm font-bold outline-none focus-visible:ring-2"
      >
        Uporedi trenutno dostupne terapeute
        <ArrowRight
          size={18}
          aria-hidden="true"
          className="transition-transform group-hover:translate-x-1 motion-reduce:transition-none"
        />
      </Link>
    </section>
  );
}
